import { useState } from "react";
import "@/components/loading/hit-loader.css";
import { Link } from "react-router-dom";
import { cardName } from "@/lib/api";
import { makeCardImgFallback } from "@/lib/imageFallback";
import { isSealed, sealedLabel } from "@/lib/productType";
import { rarityTier, VARIANT_LABEL, VARIANT_TIER_OF, TIER_CHIP, TIER_GLOW, ygoRarityLabel } from "@/lib/rarityTier";

const bundleLabel = (v) => ({ bulk: "Bulk", deck: "Deck", base: "Base" }[(v.card?.bundle_subtype || "").toLowerCase()] || "Grupo");

// Decide etiqueta + tier del chip superior izquierdo.
export function chipFor(v) {
  const variant = v.variant || "normal";
  if (isSealed(v)) return { label: sealedLabel(v), tier: "product", kind: "sealed" };
  if (variant === "bundle") return { label: bundleLabel(v), tier: "product", kind: "bundle" };
  if (v.yugioh_rarity) return { label: ygoRarityLabel(v), tier: rarityTier(v.yugioh_rarity), kind: "yugioh" };
  if (variant !== "normal") {
    const pat = variant === "patron" && v.card?.pattern_label ? ` · ${v.card.pattern_label}` : "";
    return { label: `${VARIANT_LABEL[variant] || variant}${pat}`, tier: VARIANT_TIER_OF(variant), kind: "variant" };
  }
  const r = v.card?.rarity;
  if (r && String(r).toLowerCase() !== "none" && String(r).trim()) return { label: r, tier: rarityTier(r), kind: "rarity" };
  return { label: "Carta", tier: "neutral", kind: "generic" };
}

export const HitCardTile = ({ v, to, imgSrc, eager, testid, from }) => {
  const name = cardName(v.card, v.language);
  const chip = chipFor(v);
  const out = v.in_stock === false;
  const gradedKey = v.graded_key || "raw";
  const [imgFailed, setImgFailed] = useState(false);
  const [imgLoaded, setImgLoaded] = useState(false);
  return (
    <Link to={to} state={from ? { from } : undefined} data-testid={testid}
      className="sx group hit-tile-hover block bg-[#101010] border border-white/10 rounded-[18px] md:rounded-[20px] p-1.5 md:p-2 transition-[transform,border-color] duration-200">
      <div className={`relative aspect-[63/88] rounded-xl overflow-hidden bg-[#0a0a0a] ${out ? "" : TIER_GLOW[chip.tier]}`}>
        {imgSrc && !imgFailed ? (
          <>
          {!imgLoaded && <div aria-hidden className="hl-sweep absolute inset-0" data-testid={`catalog-img-loading-${v.card_id}`} />}
          <img src={imgSrc} alt={name} loading={eager ? "eager" : "lazy"} decoding="async"
            style={out ? { filter: "grayscale(.85) brightness(.6)" } : undefined}
            className="relative w-full h-full object-cover opacity-100"
            onLoad={() => setImgLoaded(true)}
            onError={makeCardImgFallback(() => setImgFailed(true))} />
          </>
        ) : (
          <div className="absolute inset-0 grid place-items-center bg-[#141414]" data-testid={`catalog-noimg-${v.card_id}`}>
            <img src="/hit-isotipo.png" alt="" className="w-1/3 object-contain grayscale" style={{ opacity: 0.2 }} />
          </div>
        )}
        <div className="absolute inset-x-0 top-0 h-[18%] bg-gradient-to-b from-black/70 to-transparent pointer-events-none" />
        <span className={`sx-mono absolute top-1.5 left-1.5 max-w-[70%] truncate text-[8px] uppercase tracking-[.08em] px-1.5 py-[3px] rounded-md border ${TIER_CHIP[chip.tier]} ${out ? "opacity-50" : ""}`}
          title={chip.label} data-testid={`catalog-chip-${chip.kind}-${v.card_id}`} data-tier={chip.tier}>{chip.label}</span>
        <span className="sx-mono absolute top-1.5 right-1.5 text-[8px] uppercase px-1.5 py-[3px] rounded-md bg-[#FF4438] text-[#080808]">{(v.language || "").toUpperCase()}</span>
        {v.has_first_edition && (
          <span className="sx-mono absolute bottom-1.5 right-1.5 text-[8px] font-bold uppercase px-1.5 py-[3px] rounded-md bg-[#fbbf24] text-[#080808]" data-testid={`catalog-first-edition-${v.card_id}-${gradedKey}`}>1.ª ED.</span>
        )}
        {v.graded && (
          <span className="sx-mono absolute bottom-1.5 left-1.5 text-[8px] uppercase px-1.5 py-[3px] rounded-md bg-[#fbbf24] text-[#080808]" data-testid={`catalog-graded-badge-${v.card_id}-${gradedKey}`}>
            {v.graded_house} {v.graded_grade}
          </span>
        )}
        {out && (
          <span className="sx-mono absolute bottom-0 inset-x-0 text-center text-[9px] uppercase tracking-[.14em] py-1.5 bg-[rgba(8,8,8,.88)] border-t border-[rgba(255,255,255,.14)] text-white/70" data-testid={`catalog-no-stock-${v.card_id}`}>Sin stock</span>
        )}
      </div>
      <div className="px-1.5 pt-2.5 pb-1.5">
        <div className="text-[14px] font-bold leading-tight truncate tracking-[-.01em]">{name}</div>
        <div className="text-[11px] text-white/50 truncate mt-0.5">{(v.yugioh_set_code ? (v.yugioh_set_name || v.card?.set_name) : (v.card?.set_name || v.yugioh_set_name)) || "—"}{v.yugioh_set_code ? ` · ${v.yugioh_set_code}` : ""}</div>
        <div className="mt-2 flex items-end justify-between gap-2">
          <div className="min-w-0">
            <div className="sx-mono text-[8px] uppercase tracking-[.14em] text-white/45">{out ? "Últ. mercado" : "Desde"}</div>
            <div className={`text-[21px] font-extrabold leading-none tracking-[-.03em] ${out ? "text-white/50" : "text-[#FF4438]"}`}>
              {out ? (v.market_avg != null ? `$${v.market_avg.toFixed(2)}` : "—") : `$${(v.from_price ?? v.min_price ?? 0).toFixed(2)}`}
            </div>
          </div>
          {!out && (
            <div className="sx-mono text-[9px] uppercase text-white/50 text-right shrink-0">
              {v.market_avg != null ? `Merc. $${v.market_avg.toFixed(2)}` : `${v.seller_count} vendedor${v.seller_count === 1 ? "" : "es"}`}
            </div>
          )}
        </div>
      </div>
    </Link>
  );
};
